import React, { useState, useEffect, memo } from 'react';
import { portfolioConfig } from '../config/portfolio';
import { Icon } from './Icons';

// Memoized Education item component inside same file
const EducationItem = memo(({ edu, isVisible, index }) => {
  return (
    <div
      key={edu.id}
      className="relative education-item"
      data-index={index}
      style={{
        transition: 'all 0.7s ease',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(40px)'
      }}
    >
      <div className="flex items-start gap-8">
        {/* Timeline dot positioned at text level */}
        <div className="relative z-10 w-16 h-16 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg mt-2">
          <div
            className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full 
                       animate-[ping_3s_cubic-bezier(0,0,0.2,1)_infinite] opacity-10 hover:opacity-15"
          />
          <Icon name={edu.icon} className="text-black" />
        </div>

        {/* Content */}
        <div className="flex-1 bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 border border-cyan-500/20 hover:border-cyan-500/40 transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl hover:shadow-cyan-500/10 group">
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Main content */}
            <div className="lg:col-span-2">
              <h3 className="text-xl font-bold text-white mb-2">
                {edu.degree}
              </h3>
              <h4 className="text-lg text-cyan-400 font-semibold mb-2">
                {edu.institution}
              </h4>
              <p className="text-gray-300">{edu.location}</p>
              {/* GPA/CGPA line */}
              <div className="text-gray-300 opacity-0 w-0 h-0 group-hover:opacity-100 group-hover:h-auto group-hover:w-auto group-hover:mt-4">
                <strong className="text-white">{edu.grade}</strong>
              </div>
            </div>

            {/* Year in compact format */}
            <div className="space-y-4 flex justify-start lg:justify-end h-min">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-cyan-500/20 to-blue-600/20 border border-cyan-500/30 rounded-full">
                <span className="text-cyan-400 font-semibold text-sm">{edu.year}</span>
                {edu.status === 'Ongoing' && (
                  <span className="px-2 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs rounded-full">
                    {edu.status}
                  </span>
                )}
              </div>

              {/* Additional info for ongoing education */}
              {edu.status === 'Ongoing' && edu.highlights && (
                <div className="bg-gray-800/50 rounded-lg p-4 border border-cyan-500/10">
                  <h5 className="text-cyan-400 font-semibold mb-2">Highlights</h5>
                  <ul className="space-y-1 text-gray-300 text-sm">
                    {edu.highlights.map((highlight, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <svg className="w-3 h-3 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {highlight}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

const Education = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [visibleItems, setVisibleItems] = useState([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            if (entry.target.classList.contains('education-item')) {
              const index = parseInt(entry.target.dataset.index);
              setVisibleItems(prev => {
                if (!prev.includes(index)) return [...prev, index];
                return prev;
              });
            }
          }
        });
      },
      { threshold: 0.1 }
    );

    const section = document.getElementById('education');
    if (section) observer.observe(section);

    const items = document.querySelectorAll('.education-item');
    items.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const section = document.getElementById('education');
      if (section) {
        const sectionTop = section.offsetTop;
        const sectionHeight = section.offsetHeight;
        const scrollY = window.scrollY;
        const windowHeight = window.innerHeight;

        const sectionStart = sectionTop - windowHeight * 0.5;
        const sectionEnd = sectionTop + sectionHeight - windowHeight * 0.5;

        if (scrollY >= sectionStart && scrollY <= sectionEnd) {
          const progress = Math.min(100, Math.max(0,
            ((scrollY - sectionStart) / (sectionEnd - sectionStart)) * 100
          ));
          setScrollProgress(progress);
        } else if (scrollY < sectionStart) {
          setScrollProgress(0);
        } else if (scrollY > sectionEnd) {
          setScrollProgress(100);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section id="education" className="py-20 bg-gray-800/50 relative">
      <div className="container mx-auto px-8">
        <div className={`transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h2 className="text-4xl lg:text-5xl font-bold text-center mb-16">
            <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              Education
            </span>
          </h2>

          <div className="max-w-4xl mx-auto">
            <div className="relative">
              {/* Enhanced Animated Timeline line with extended length and fade effects */}
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-600/50 -mt-16 -mb-16">
                {/* Top fade effect - pre-filled */}
                <div className="absolute -top-16 left-0 w-full h-16 bg-gradient-to-b from-transparent via-cyan-500/40 to-cyan-500/60"></div>

                {/* Main line with pre-filled top portion */}
                <div className="absolute top-0 left-0 w-full h-16 bg-gradient-to-b from-cyan-500/60 to-cyan-500/80"></div>

                {/* Progress line */}
                <div
                  className="absolute top-16 left-0 w-full transition-all duration-300 ease-out"
                  style={{
                    height: `calc(${scrollProgress}% - 64px)`,
                    background: `linear-gradient(to bottom, 
                      rgba(6, 182, 212, 0.8) 0%, 
                      rgba(59, 130, 246, 0.8) 100%)`
                  }}
                ></div>

                {/* Bottom fade effect - fillable */}
                <div
                  className="absolute -bottom-16 left-0 w-full h-24 transition-all duration-300 ease-out"
                  style={{
                    background: scrollProgress > 92
                      ? `linear-gradient(to bottom, 
                          rgba(59, 130, 246, ${Math.min(0.8, (scrollProgress - 92) / 10 * 0.8)}) 25%, 
                          rgba(59, 130, 246, ${Math.min(0.4, (scrollProgress - 92) / 10 * 0.4)}) 50%,
                          transparent 100%)`
                      : `linear-gradient(to bottom, 
                          transparent 0%, 
                          rgba(156, 163, 175, 0.2) 50%, 
                          transparent 100%)`
                  }}
                ></div>
              </div>

              {/* Education items */}
              <div className="space-y-12">
                {portfolioConfig.education.map((edu, index) => (
                  <EducationItem
                    key={edu.id}
                    edu={edu}
                    index={index}
                    isVisible={visibleItems.includes(index)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Education;